import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';

import { Store } from '@ngrx/store';
import * as fromRoot from '../../reducers';
import * as CoreActions from '../../actions/core.actions';
import * as AuthActions from '../../actions/auth.actions';
import * as FileListActions from '../../actions/file-list.actions';

import { ApiService } from '../../core/api.service';
import { SyncCookieService } from '../../core/sync-cookie.service';
import { Base64Service } from '../../core/base64.service';
import { SyncCryptService } from '../../core/crypt/sync-crypt.service';
import { SyncDigestService } from '../../core/crypt/sync-digest.service';
import { ERROR_CODES, AuthSSO, User, AuthUserPassword, ErrCode, PasswordUpdatePayload } from '../../shared/models';
import {
  GetloginsaltApiOutput,
  GetUserKeysApiOutput,
  SessionNewApiOutput,
  SessionNewPayload,
  UserSsoApiOutput,
  UserSsoApiInput,
} from '../../shared/models/api';
import { LoggerService } from '../../core/logger.service';
import { UserService } from '../../core/user.service';
import { NotificationsService } from '../../core/notifications.service';
import { BroadcastService } from 'src/app/shared/services/broadcast.service';
import { TransferService } from 'src/app/transfer/services/queue/transfer.service';
import { WebSocketService } from 'src/app/core/websocket.service';
import { FolderStatsCacheService } from 'src/app/core/folder-stats-cache.service';
import { ThumbnailCacheService } from 'src/app/file-list/services/thumbnail-cache.service';
import { WebauthLoginResult } from '../shared/types';
import { State } from 'src/app/reducers/auth.reducer';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authenticated = false;
  private bCrypt: bCrypt;
  constructor(
    private api: ApiService,
    private base64: Base64Service,
    private crypt: SyncCryptService,
    private digest: SyncDigestService,
    private log: LoggerService,
    private store: Store<fromRoot.State>,
    private userService: UserService,
    private transferService: TransferService,
    private notificationsService: NotificationsService,
    private broadcastService: BroadcastService,
    private webSocketService: WebSocketService,
    private syncCookie: SyncCookieService,
    private folderStatsService: FolderStatsCacheService,
    private thumbnailCacheService: ThumbnailCacheService,
  ) {
    this.bCrypt = new bCrypt();
  }

  // helper for ng1 to get store data.
  public authState(): Observable<State> {
    return this.store.select(fromRoot.getAuthState);
  }

  // helper for ng1 to get store data. share-provision-controller
  public login(username, password, twofacode?, cfTurnstileToken?: string): void {
    const item = new AuthUserPassword(username, password, twofacode, undefined, undefined, cfTurnstileToken);
    this.store.dispatch(new AuthActions.LoginAction(item));
  }

  public async loginUserPass(
    username: string,
    password: string,
    twofaCode?: string,
    cfTurnstileToken?: string,
  ): Promise<User> {
    username = username.toLowerCase();
    return await this.authenticate(username, password, twofaCode, undefined, cfTurnstileToken);
  }

  public async authenticate(
    username: string,
    password: string,
    twofaCode?: string,
    bypass2fa?: boolean,
    cfTurnstileToken?: string,
  ): Promise<User> {
    username = username.toLowerCase();
    const hashPass = await this.getHashedPassword(username, password);

    try {
      const accessData = await this.performSessionNewLogin(password, {
        username,
        password: hashPass,
        twofacode: twofaCode,
        bypass2fa,
        ...(cfTurnstileToken ? { cf_turnstile_token: cfTurnstileToken } : {}),
      });
      return new User(accessData);
    } catch (ex) {
      this.log.error('authenticate() failed');
      this.log.error(ex);
      throw ErrCode.fromException(ex);
    }
  }

  public async authenticateWebauth(
    username: string,
    password: string,
    twofacode?: string,
  ): Promise<WebauthLoginResult> {
    username = username.toLowerCase();
    const hashPass = await this.getHashedPassword(username, password);
    const authInput = {
      username,
      password: hashPass,
      twofacode,
    };
    try {
      const appSession = await this.api.send<SessionNewApiOutput>('sessionnewagent', authInput);
      const hasAgentErrors = appSession.success == 1 && appSession.errors;

      if (hasAgentErrors) {
        return { errorCode: appSession.error_code };
      }

      return { appSession };
    } catch (ex) {
      this.log.error('authenticate webauth() failed');
      this.log.error(ex);
      throw ErrCode.fromException(ex);
    }
  }

  public async authenticateWebauthCpToken(
    username: string,
    password: string,
    twofacode?: string,
    cfTurnstileToken?: string,
    deviceTypeId?: number,
  ): Promise<WebauthLoginResult> {
    username = username.toLowerCase();
    const hashPass = await this.getHashedPassword(username, password);
    try {
      const accessData = await this.performSessionNewLogin(password, {
        username,
        password: hashPass,
        twofacode,
        ...(deviceTypeId ? { device_type_id: deviceTypeId } : {}),
        ...(cfTurnstileToken ? { cf_turnstile_token: cfTurnstileToken } : {}),
      });
      return { appSession: accessData };
    } catch (ex) {
      this.log.error('authenticate webauth (CPToken) failed');
      this.log.error(ex);
      throw ErrCode.fromException(ex);
    }
  }

  public async authenticateAndGetUserKeys(
    username: string,
    hashPass: string,
    twofaCode?: string,
    isLegacyToCNC?: boolean,
  ): Promise<GetUserKeysApiOutput> {
    username = username.toLowerCase();
    try {
      let accessData;
      if (isLegacyToCNC !== undefined && isLegacyToCNC === true) {
        accessData = await this.api.send<SessionNewApiOutput>('userverifypassword', {
          username: username,
          password: hashPass,
          isLegacyToCNC,
        });
      } else {
        accessData = await this.api.send<SessionNewApiOutput>('sessionnew', {
          username: username,
          password: hashPass,
          twofacode: twofaCode,
        });
      }

      if (accessData.success == 1 && accessData.errors) {
        throw ErrCode.fromApiLegacy(accessData.errors);
      }
      const keys = await this.api.send<GetUserKeysApiOutput>('getuserkeys', {
        username: username,
        password: hashPass,
        isLegacyToCNC: isLegacyToCNC ? true : false,
      });
      return keys;
    } catch (ex) {
      throw ErrCode.fromException(ex);
    }
  }

  public async loginSSO(loginData: AuthSSO): Promise<User> {
    await this.logout();

    const resp = await this.api.send<UserSsoApiOutput>('usersso', <UserSsoApiInput>{
      key: loginData.lookupkey,
      twofacode: loginData.twofacode,
    });
    if (resp && resp.success == 1) {
      await this.storeKeys(resp, loginData.password);
      await this.storeAccessData(resp.userattr);
    }
    // startNotificationLoop
    this.notificationsService.startNotificationLoop();
    return new User(resp.userattr);
  }

  private async performSessionNewLogin(password: string, payload: SessionNewPayload): Promise<SessionNewApiOutput> {
    const accessData = await this.api.send<SessionNewApiOutput>('sessionnew', payload);
    if (accessData.success == 1 && accessData.errors) {
      throw ErrCode.fromApiLegacy(accessData.errors);
    }
    const keys = await this.api.send<GetUserKeysApiOutput>('getuserkeys', {
      username: payload.username,
      password: payload.password,
    });
    await this.storeKeys(keys, password);
    await this.storeAccessData(accessData);
    return accessData;
  }

  private async storeAccessData(accessData: AccessTokens): Promise<boolean> {
    const secret = await this.crypt.storeEncrypt(accessData.access_secret);
    this.store.dispatch(
      new CoreActions.SetValueAction({
        key: 'access_token',
        value: accessData.access_token,
      }),
    );
    this.store.dispatch(
      new CoreActions.SetValueAction({
        key: 'access_secret',
        value: secret,
      }),
    );
    return true;
  }

  private async storeKeys(keys: UserKeys, password: string): Promise<boolean> {
    try {
      const meta = await this.crypt.userkeyDecrypt(keys.enc_meta_key, password);
      const priv = await this.crypt.userkeyDecrypt(keys.enc_priv_key, password);
      const encMeta = await this.crypt.storeEncrypt(meta);
      const encPriv = await this.crypt.storeEncrypt(priv);
      this.store.dispatch(new CoreActions.SetValueAction({ key: 'meta_key', value: encMeta }));
      this.store.dispatch(
        new CoreActions.SetValueAction({
          key: 'private_key',
          value: encPriv,
        }),
      );
      return true;
    } catch (ex) {
      this.log.e('Error storing keys on user, key decryption failed', ex);
      throw new ErrCode(ERROR_CODES.LOGIN_ERROR);
    }
  }

  public storeJwtToken(token: string): void {
    this.store.dispatch(
      new CoreActions.SetValueAction({
        key: 'multiadmin_jwt_token',
        value: token,
      }),
    );
  }

  public isAuthenticated(): Observable<boolean> {
    return of(this.authenticated);
  }

  public async getHashedPassword(username: string, password: string, noUpdate?: boolean): Promise<string> {
    try {
      const data = await this.api.send<GetloginsaltApiOutput>('getloginsalt', {
        username_b64: this.base64.encode(username),
      });
      return await this.hashPassword((<GetloginsaltApiOutput>data).salt, password, noUpdate);
    } catch (ex) {
      this.log.e('getHashedPassword username or password does not exist', ex);
      throw new ErrCode(ERROR_CODES.LOGIN_CREDENTIALS_INCORRECT);
    }
  }

  public hashPassword(salt: string, password: string, noUpdate?: boolean): Promise<string> {
    return new Promise((resolve, reject) => {
      const sha1pass = this.digest.hash(password),
        mangle = this.digest.hash256(sha1pass + sha1pass.substring(4, 24));
      let number = 0;
      this.bCrypt.hashpw(
        mangle,
        salt,
        (hashedPass: string) => {
          resolve(hashedPass);
        },
        () => {
          if (noUpdate) {
            return;
          }
          number += 1;
          this.store.dispatch(new AuthActions.LoginProgressAction(number));
        },
      );
    });
  }

  public async persistSessionCookie(): Promise<void> {
    const data = await firstValueFrom(this.store.select(fromRoot.getSignApiData));
    const values = {
      access_token: data.access_token,
      access_secret: await this.crypt.storeDecrypt('access_secret'),
      uid: data.uid,
      web_device_id: data.web_device_id,
    };
    this.syncCookie.setAuthSession(this.base64.encode(Object.values(values).join('|')));
  }

  /**
   * @ngdoc method
   * @name  logout
   * @methodOf sync.service:AuthService
   * @description
   * Logs a user out and resets the state of the cp.
   *
   */
  public async logout(): Promise<void> {
    this.log.info('Logging out user');
    this.syncCookie.deleteAuthSession();
    this.notificationsService.handleLogout();
    if (this.userService.isAuthenticated()) {
      try {
        await this.api.execute('sessiondelete', {});
      } catch (ex) {
        this.log.warn('Error logging out');
        this.log.warn(ex);
      }
    }
    this.store.dispatch(new CoreActions.StoreClearAction());
    this.store.dispatch(new AuthActions.LogoutAction());
    this.store.dispatch(new FileListActions.FileListResetAction());
    this.transferService.reset();
    this.folderStatsService.clear();
    this.thumbnailCacheService.clear();
    this.broadcastService.broadcast('sync.reset.user');
    this.webSocketService.close();
  }

  public async resetWeakPassword(
    username: string,
    password: string,
    newPassword: PasswordUpdatePayload,
  ): Promise<void> {
    username = username.toLowerCase();
    const hashPass = await this.getHashedPassword(username, password);
    try {
      await this.api.send<SessionNewApiOutput>('passwordupdate', {
        username: username,
        password: hashPass,
        ...newPassword,
      });
    } catch (ex) {
      this.log.error('authenticate() failed');
      this.log.error(ex);
      throw ErrCode.fromException(ex);
    }
  }
}
